import React, { useRef, useState } from "react";
import { AiOutlineFileExcel } from "react-icons/ai";
import toast from "react-hot-toast";
import { fetchDataV2 } from "../../../store/config";

const ExcelsPage: React.FC = () => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const handleButtonClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setErrorMessage(null);
      setSuccessMessage(null);
      const isExcel = file.name.endsWith(".xlsx") || file.name.endsWith(".xls");
      if (!isExcel) {
        setErrorMessage("Please select a valid Excel file (.xlsx or .xls)");
        return;
      }
      setSelectedFile(file);
      toast.success(`File "${file.name}" selected`);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage(null);
    setSuccessMessage(null);
    if (!selectedFile) {
      setErrorMessage("Select a file before importing");
      return;
    }

    const loadingToast = toast.loading("Importing data...");
    setUploading(true);

    try {
      const formData = new FormData();
      formData.append("file", selectedFile);

      const response = await fetchDataV2().post(
        "/employees/import-excel",
        formData,
      );

      if (response.data.success) {
        toast.dismiss(loadingToast);
        setSuccessMessage(
          response.data.message || "Import completed successfully",
        );
        setSelectedFile(null);
        if (fileInputRef.current) fileInputRef.current.value = "";
      } else {
        toast.dismiss(loadingToast);
        setErrorMessage(
          response.data?.detail ||
            response.data?.message ||
            "Error importing data",
        );
      }
    } catch (error: any) {
      toast.dismiss(loadingToast);
      const msg = error.response?.data?.detail || "Error connecting to server";
      setErrorMessage(msg);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="p-excels">
      <div className="p-excels__content">
        <form className="p-excels__card" onSubmit={handleSubmit}>
          <div className="p-excels__card-icon">
            <AiOutlineFileExcel size={48} color="#1D6F42" />
          </div>
          <h2 className="p-excels__card-title">Import Data</h2>
          <p className="p-excels__card-description">
            Select an Excel file to upload new employees to the system.
          </p>

          {errorMessage && (
            <div className="p-excels__error-msg">
              {errorMessage.split("\n").map((line, i) => {
                const trimmed = line.trim();
                if (trimmed.startsWith("✅")) {
                  return (
                    <div
                      key={i}
                      className="p-excels__error-item p-excels__error-item--success"
                    >
                      {trimmed}
                    </div>
                  );
                }
                if (trimmed.startsWith("❌")) {
                  return (
                    <div
                      key={i}
                      className="p-excels__error-item p-excels__error-item--error"
                    >
                      {trimmed}
                    </div>
                  );
                }
                if (trimmed.startsWith("⚠️")) {
                  return (
                    <div
                      key={i}
                      className="p-excels__error-item p-excels__error-item--warning"
                    >
                      {trimmed}
                    </div>
                  );
                }
                return (
                  <div
                    key={i}
                    className="p-excels__error-title"
                    style={{ margin: 0 }}
                  >
                    {line}
                  </div>
                );
              })}
            </div>
          )}

          {successMessage && (
            <p className="p-excels__success-msg">{successMessage}</p>
          )}
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileChange}
            accept=".xlsx, .xls"
            style={{ display: "none" }}
          />

          {!selectedFile ? (
            <button
              type="button"
              className="c-button c-button--primary"
              onClick={handleButtonClick}
            >
              Select Excel
            </button>
          ) : (
            <div className="p-excels__actions">
              <p className="p-excels__filename">File: {selectedFile.name}</p>
              <div className="p-excels__buttons">
                <button
                  type="submit"
                  className="c-button c-button--primary"
                  disabled={uploading}
                >
                  {uploading ? "Uploading..." : "Confirm Import"}
                </button>
                <button
                  type="button"
                  className="c-button c-button--secondary"
                  onClick={() => {
                    setSelectedFile(null);
                    if (fileInputRef.current) fileInputRef.current.value = "";
                  }}
                  disabled={uploading}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

export default ExcelsPage;
